import { ChevronsUpDown } from 'lucide-react';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User } from '@/utils/types';
import AuthService from '@/services/auth/auth.service';
import { REMOVE_USER } from '@/store/types';
import { ROUTES } from '@/utils/constants';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { logoutEvent } from '@/lib/ga/headers.events';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import useInIframe from '@/hooks/useInIframe';
import LogoutIcon from '../../../public/icons/new-dashboard/log-out.svg';
import VideoIcon from '../../../public/icons/new-dashboard/video.svg';
import PhoneIcon from '../../../public/icons/new-dashboard/phone.svg';
import MailIcon from '../../../public/icons/new-dashboard/mail.svg';
import UserIcon from '../../../public/icons/new-dashboard/user.svg';

export function NavUser({
  user,
  workspaceAdminDetails,
}: {
  user: User;
  workspaceAdminDetails: any;
}) {
  const { isMobile } = useSidebar();
  const router = useRouter();
  const dispatch = useDispatch();
  const inIframe = useInIframe();

  const [menuOpen, setMenuOpen] = useState(false)

  const handleLogoutClick = async () => {
    try {
      await AuthService.logout({});

      const element = document.getElementById('ch-call-icn');
      if (element) {
        element.style.display = 'none';
      }
      logoutEvent();
      dispatch({ type: REMOVE_USER });
    } catch (err) {
      console.log(err);
    } finally {
      localStorage.removeItem('user_data');
      localStorage.removeItem('auth_provider');
      localStorage.removeItem('identities');
      localStorage.removeItem('workspaces_data');
      localStorage.removeItem('selected_workspace');
      localStorage.removeItem('availability_toggle');
      localStorage.removeItem('zipme-auth');
      sessionStorage.clear();
      router.push(`/auth/${ROUTES.AUTH_TYPES.LOGIN}`);
    }
  };

  useEffect(() => {
    const onRouteChange = () => setMenuOpen(false)
    router.events.on("routeChangeStart", onRouteChange)
    return () => {
      router.events.off("routeChangeStart", onRouteChange)
    }
  }, [router.events])

  return (
    <SidebarMenu>
      {user?.first_name && (
        <SidebarMenuItem>
          <DropdownMenu  open={menuOpen} onOpenChange={setMenuOpen} >
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-full">
                  <AvatarImage
                    src={user?.avatar_url}
                    alt={user?.first_name}
                  />
                  <AvatarFallback className="rounded-full">
                    <Image
                      src="/icons/profile-default.svg"
                      className="rounded-full w-[30px] h-[30px] object-cover"
                      height={30}
                      width={30}
                      alt="profile"
                      onError={e => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/icons/profile-default.svg';
                      }}
                    />
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg ml-1.5"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuItem className="p-0 font-normal" onSelect={() => setMenuOpen(false)}>
                <Link href="/workspace?selected=my_profile">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm rounded-sm cursor-pointer">
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarImage
                        src={user?.avatar_url}
                        alt={user?.first_name}
                      />
                      <AvatarFallback className="rounded-full">
                        <Image
                          src="/icons/profile-default.svg"
                          className="rounded-full w-[30px] h-[30px] object-cover"
                          height={30}
                          width={30}
                          alt="profile"
                          onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/icons/profile-default.svg';
                          }}
                        />
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user?.first_name} {user?.last_name}
                      </span>
                      <span className="truncate text-xs">{user?.email}</span>
                    </div>
                  </div>
                </Link>
              </DropdownMenuItem>
              {!inIframe && workspaceAdminDetails && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup className="p-2">
                    <p className="font-medium text-xs text-muted py-1.5">
                      Your Admin
                    </p>
                    <div className="px-2 py-1.5 flex gap-2 items-center">
                      <UserIcon />
                      <p className="text-sm mb-0.5">
                        {workspaceAdminDetails?.first_name}{' '}
                        {workspaceAdminDetails?.last_name || ''}
                      </p>
                    </div>
                    <Link
                      href={`mailto:${workspaceAdminDetails?.email}`}
                    >
                      <div className="px-2 py-1.5 flex gap-2 items-center underline rounded-sm hover:bg-slate-100 cursor-pointer">
                        <MailIcon />
                        <p className="text-sm mb-0.5">
                          {workspaceAdminDetails?.email}
                        </p>
                      </div>
                    </Link>

                    <p className="font-medium text-xs text-muted py-1.5">
                      Our Support
                    </p>
                    <Link
                      href="mailto:hello@zipteams.com"
                    >
                      <div className="px-2 py-1.5 flex gap-2 items-center underline rounded-sm hover:bg-slate-100 cursor-pointer">
                        <MailIcon />
                        <p className="text-sm mb-0.5">hello@zipteams.com</p>
                      </div>
                    </Link>
                    <div className="px-2 py-1.5 flex gap-2 items-center">
                      <PhoneIcon />
                      <p className="text-sm mb-0.5">+91 9653198284</p>
                    </div>
                    <Link
                      href="https://zipme.at/zipteams"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <div className="px-2 py-1.5 flex gap-2 items-center underline rounded-sm hover:bg-slate-100 cursor-pointer">
                        <VideoIcon />
                        <p className="text-sm mb-0.5">zipme.at/zipteams</p>
                      </div>
                    </Link>
                  </DropdownMenuGroup>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="m-2 mb-1 p-2 flex items-center cursor-pointer"
                onClick={handleLogoutClick}
              >
                <LogoutIcon />
                <p className="text-[#FF0000]">Log out</p>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      )}
      
    </SidebarMenu>
  );
}
